import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Send, Loader2, Sparkles } from "lucide-react";

const WELCOME = {
  role: "assistant",
  content:
    "Hi, I'm Cody 👋 your Maqam concierge. Ask me how to build a CV, apply to 100 jobs in one click, connect your Gmail, practice with Marcus, or anything else — I'll walk you through it.",
};

export default function CodySupport() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll chat on new message.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  // Focus input when panel opens.
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 120);
  }, [open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const r = await api.post("/support/cody", { message: text, session_id: sessionId });
      setSessionId(r.data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply }]);
    } catch {
      toast.error("Cody is offline — please try again");
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Cody — shiny metallic orb (white/gray/black), pure CSS, no 3D */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close Cody support" : "Open Cody support"}
        className="cody-orb fixed bottom-20 right-5 z-[60] w-12 h-12 rounded-full cursor-pointer hover:scale-110 active:scale-95 transition-transform group"
        data-testid="cody-support-button"
      >
        <span className="absolute inset-0 rounded-full animate-ping bg-white/10 pointer-events-none" />
        {/* Always-visible "need help?" banner */}
        {!open && (
          <span
            className="cody-banner absolute top-1/2 -translate-y-1/2 right-[calc(100%+10px)] bg-gradient-to-r from-gray-200 via-white to-gray-400 text-[#0a0a0a] text-xs font-bold px-3 py-1.5 rounded-full shadow-[0_4px_14px_rgba(0,0,0,0.4)] whitespace-nowrap pointer-events-none"
            data-testid="cody-need-help-banner"
          >
            need help?
            <span className="absolute top-1/2 -translate-y-1/2 -right-1 w-2 h-2 rotate-45 bg-gray-400" />
          </span>
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-36 right-5 z-[60] w-[min(92vw,380px)] h-[min(65vh,520px)] bg-[#0f0f11] border border-white/15 rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.6)] flex flex-col overflow-hidden"
          data-testid="cody-chat-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-gradient-to-r from-white/5 to-transparent">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-white/20 to-white/5 border border-white/20 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="font-bold text-sm text-white leading-tight">Cody</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">
                  Maqam support · online
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1.5 hover:bg-white/10 rounded transition-colors"
              aria-label="Close"
              data-testid="cody-close"
            >
              <X className="w-4 h-4 text-white/70" />
            </button>
          </div>

          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3 text-sm"
            data-testid="cody-messages"
          >
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-white text-black rounded-br-sm"
                      : "bg-white/8 text-white/90 border border-white/10 rounded-bl-sm"
                  }`}
                  data-testid={`cody-msg-${m.role}`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white/8 border border-white/10 rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: "120ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: "240ms" }} />
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-white/10 p-3 bg-[#09090b]">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                rows={1}
                placeholder="Ask Cody anything about Maqam…"
                className="flex-1 resize-none bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-white/30 max-h-24"
                data-testid="cody-input"
              />
              <button
                onClick={send}
                disabled={sending || !input.trim()}
                className="w-9 h-9 flex items-center justify-center bg-white text-black rounded-xl disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white/90 transition-colors shrink-0"
                data-testid="cody-send"
                aria-label="Send"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/30 mt-2 text-center">
              Cody knows every feature · Enter to send
            </div>
          </div>
        </div>
      )}
    </>
  );
}
